import type { ProspectEntry } from '@common/types/prospects';
import { getMatterTypeLabel, getPracticeAreaLabel } from '@common/util';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, InputAdornment, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChangeEvent, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AlertDialog from '@/components/AlertDialog';
import ApsDataExtractionDiscoveryBanner from '@/components/ApsDataExtractionDiscoveryBanner';
import LoadingButton from '@/components/Buttons/LoadingButton';
import DataTable, { type Column } from '@/components/DataTable';
import FilterChip from '@/components/FilterChip';
import MultiCheckSelect from '@/components/MultiCheckSelect';
import NewProspect from '@/components/NewProspectModal/NewProspect';
import PageWrapper from '@/components/PageWrapper';
import QuoteLinkSentModal from '@/components/QuoteLinkSent/QuoteSentModal';
import { STATUS_OPTIONS } from '@/constants';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useUsers } from '@/queries/users';
import { deleteProspects, getAllProspects } from '@/services/ProspectService';
import useApsDiscoveryStore from '@/stores/useApsDiscoveryStore';
import { convertUTCDateToEST, truncate } from '@/utils';
import { getProspectStatusMeta } from '@/utils/prospectDisplay';
import { successSnackbar } from '@/utils/snackbar';

export default function Prospects() {
    const theme = useTheme();
    const queryClient = useQueryClient();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const { activeLawFirmId } = useLawFirmContext();

    const isDeleteButtonDisabled = selectedRows.length === 0;

    const [searchKey, setSearchKey] = useState('');
    const [matterTypeFilter, setMatterTypeFilter] = useState<string[]>([]);
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [assignedLawyerFilter, setAssignedLawyerFilter] = useState<string[]>([]);

    const [isNewIntakeModalOpen, setIsNewIntakeModalOpen] = useState(false);
    const [isNewQuoteAndIntakeModalOpen, setIsNewQuoteAndIntakeModalOpen] = useState(false);
    const [prefillPurchaseFromDiscovery, setPrefillPurchaseFromDiscovery] = useState(false);
    const pendingTryItNow = useApsDiscoveryStore((state) => state.pendingTryItNow);
    const consumeTryItNow = useApsDiscoveryStore((state) => state.consumeTryItNow);
    const clearApsScanHighlight = useApsDiscoveryStore((state) => state.clearApsScanHighlight);

    const currentQueryKey = ['prospects', { lawFirmId: activeLawFirmId }];
    const {
        data: prospects = [],
        isLoading: isProspectsLoading,
        refetch: refetchProspects,
    } = useQuery({
        queryKey: currentQueryKey,
        queryFn: async () => {
            if (!activeLawFirmId) return [];
            const response = await getAllProspects(activeLawFirmId);
            return response.data;
        },
        enabled: !!activeLawFirmId,
        staleTime: 2 * 60_000,
    });

    const { data: users = [] } = useUsers({ lawFirmId: activeLawFirmId });

    useEffect(() => {
        setAssignedLawyerFilter([]);
        setSelectedRows([]);
    }, [activeLawFirmId]);

    useEffect(() => {
        if (!pendingTryItNow) {
            return;
        }
        setPrefillPurchaseFromDiscovery(true);
        setIsNewIntakeModalOpen(true);
        consumeTryItNow();
    }, [pendingTryItNow, consumeTryItNow]);

    const matterTypeFilterOptions = useMemo(() => {
        const matterTypes = [
            ...new Set(prospects.map((prospect) => prospect.matterType).filter(Boolean)),
        ];

        return matterTypes
            .sort((a, b) => getMatterTypeLabel(a).localeCompare(getMatterTypeLabel(b)))
            .map((value) => ({
                name: value,
                label: getMatterTypeLabel(value),
            }));
    }, [prospects]);

    const filteredProspects = useMemo(() => {
        return prospects.filter((prospect) => {
            const isAssignedLawyerMatched =
                assignedLawyerFilter.length === 0 ||
                (prospect.quoteApprover &&
                    assignedLawyerFilter.includes(`${prospect.quoteApprover.userId}`));
            const isMatterTypeMatched =
                matterTypeFilter.length === 0 || matterTypeFilter.includes(prospect.matterType);
            const isStatusMatched =
                statusFilter.length === 0 ||
                (prospect.prospectStatus && statusFilter.includes(prospect.prospectStatus));
            const isSearchKeyMatched =
                searchKey === '' ||
                prospect.prospectName.toLowerCase().includes(searchKey.toLowerCase()) ||
                prospect.contact.contactName.toLowerCase().includes(searchKey.toLowerCase()) ||
                prospect?.customFileNumber?.toLocaleLowerCase()?.includes(searchKey?.toLowerCase());
            return (
                isAssignedLawyerMatched &&
                isMatterTypeMatched &&
                isSearchKeyMatched &&
                isStatusMatched
            );
        });
    }, [prospects, assignedLawyerFilter, matterTypeFilter, statusFilter, searchKey]);

    useEffect(() => {
        const visibleProspectIds = new Set(filteredProspects.map((p) => p.prospectId));
        setSelectedRows((prevSelectedRows) => {
            const nextSelectedRows = prevSelectedRows.filter((id) => visibleProspectIds.has(id));
            return nextSelectedRows.length === prevSelectedRows.length
                ? prevSelectedRows
                : nextSelectedRows;
        });
    }, [filteredProspects]);

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
    };

    const columns: Column<ProspectEntry>[] = [
        {
            id: 'prospectId',
            label: 'Prospect ID',
            _render: (prospect) => (
                <Link
                    style={{ fontWeight: '500' }}
                    to={`/prospects/${prospect.prospectId}/summary`}
                >
                    {prospect.prospectName}
                </Link>
            ),
            _isSortable: true,
        },
        {
            id: 'customFileNumber',
            label: 'Custom File No.',
            _render: (prospect) => truncate(prospect.customFileNumber ?? '', 25),
            _width: '200px',
        },
        {
            id: 'contact',
            label: 'Client name',
            _render: (prospect) => prospect.contact.contactName,
            _isSortable: true,
        },
        {
            id: 'matterType',
            label: 'Matter Type',
            _render: (prospect) => getMatterTypeLabel(prospect.matterType),
            _isSortable: true,
        },
        {
            id: 'practiceArea',
            label: 'Practice Area',
            _render: (prospect) => getPracticeAreaLabel(prospect.practiceArea),
            _isSortable: true,
        },
        {
            id: 'quoteTemplate',
            label: 'Assigned user',
            _render: (prospect) =>
                users?.find((user) => user.userId === prospect.quoteApprover?.userId)?.fullName ??
                '',
            _isSortable: true,
        },
        {
            id: 'createdAt',
            label: 'Created on',
            _render: (prospect) => convertUTCDateToEST(prospect.createdAt, 'MMM d, yyyy'),
            _isSortable: true,
        },
        {
            id: 'status',
            label: 'Status',
            _render: (prospect) => {
                const currentStatus = getProspectStatusMeta(prospect.prospectStatus);
                return (
                    <Typography
                        variant="body2"
                        sx={{
                            width: 'fit-content',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            color: currentStatus?.color,
                            backgroundColor: currentStatus?.backgroundColor,
                        }}
                    >
                        {currentStatus?.label}
                    </Typography>
                );
            },
            _isSortable: true,
        },
    ];

    const filterSelected: ReactNode[] = [];
    if (searchKey) {
        filterSelected.push(<FilterChip label={searchKey} onClear={() => setSearchKey('')} />);
    }
    matterTypeFilter.forEach((val) => {
        filterSelected.push(
            <FilterChip
                label={val}
                onClear={() => setMatterTypeFilter((prev) => prev.filter((v) => v !== val))}
            />,
        );
    });
    statusFilter.forEach((val) => {
        filterSelected.push(
            <FilterChip
                label={STATUS_OPTIONS.find((st) => st.name === val)?.label || ''}
                onClear={() => setStatusFilter((prev) => prev.filter((v) => v !== val))}
            />,
        );
    });
    assignedLawyerFilter.forEach((val) => {
        filterSelected.push(
            <FilterChip
                label={users?.find((user) => `${user?.userId}` === val)?.fullName || ''}
                onClear={() => setAssignedLawyerFilter((prev) => prev.filter((v) => v !== val))}
            />,
        );
    });

    const resetFilters = () => {
        setSearchKey('');
        setMatterTypeFilter([]);
        setStatusFilter([]);
        setAssignedLawyerFilter([]);
    };

    const handleDelete = async () => {
        setOpenDeleteDialog(true);
    };
    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            await deleteProspects(ids);
            return ids;
        },
        onSuccess: (deletedIds) => {
            queryClient.setQueryData<ProspectEntry[]>(currentQueryKey, (old) => {
                if (!old) return old;
                return old.filter((p) => !deletedIds.includes(p.prospectId));
            });
            setOpenDeleteDialog(false);
            setSelectedRows([]);
            successSnackbar('Prospect(s) archived successfully');
        },
        onError: (error) => {
            console.error(error);
        },
        onSettled: () => {
            setOpenDeleteDialog(false);
        },
    });
    const deleteSelectedProspects = async () => {
        if (selectedRows.length === 0) return;
        deleteMutation.mutate(selectedRows);
    };

    const handleNewIntakeModalClose = () => {
        refetchProspects();
        setIsNewIntakeModalOpen(false);
        setPrefillPurchaseFromDiscovery(false);
        clearApsScanHighlight();
    };

    return (
        <PageWrapper
            title="Prospects"
            actions={
                <>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setPrefillPurchaseFromDiscovery(false);
                            setIsNewIntakeModalOpen(true);
                        }}
                    >
                        INTAKE
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setIsNewQuoteAndIntakeModalOpen(true)}
                    >
                        QUOTE & INTAKE
                    </Button>
                </>
            }
        >
            <Stack spacing={2}>
                <ApsDataExtractionDiscoveryBanner />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <TextField
                            sx={{
                                backgroundColor: theme.palette.neutral.N_50,
                                height: '40px',
                                boxSizing: 'border-box',
                                borderRadius: '8px',
                                color: theme.palette.neutral.gray,
                                width: '400px',
                            }}
                            placeholder="Search by prospect ID, file no. or client name"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            value={searchKey}
                            onChange={(ev) => setSearchKey(ev.target.value)}
                        />
                        <MultiCheckSelect
                            label="Matter type"
                            selected={matterTypeFilter}
                            options={matterTypeFilterOptions}
                            width="130px"
                            onSelectOptions={(value) => setMatterTypeFilter(value)}
                        />
                        <MultiCheckSelect
                            label="Status"
                            selected={statusFilter}
                            options={STATUS_OPTIONS}
                            width="90px"
                            onSelectOptions={(value) => setStatusFilter(value)}
                        />
                        <MultiCheckSelect
                            label="Assigned User"
                            selected={assignedLawyerFilter}
                            options={users.map((user) => ({
                                name: `${user.userId}`,
                                label: user.fullName,
                            }))}
                            width="165px"
                            onSelectOptions={(value) => setAssignedLawyerFilter(value)}
                        />
                    </Box>
                    <Stack direction="row" spacing={1}>
                        {!isDeleteButtonDisabled && (
                            <LoadingButton
                                loading={deleteMutation.isPending}
                                variant="contained"
                                onClick={() => handleDelete()}
                                disabled={isDeleteButtonDisabled}
                                size="medium"
                            >
                                Archive
                            </LoadingButton>
                        )}
                    </Stack>
                </Box>
                {filterSelected?.length > 0 && (
                    <Stack direction="row" spacing={2}>
                        {filterSelected}
                        <Button onClick={() => resetFilters()} size="medium">
                            Clear all
                        </Button>
                    </Stack>
                )}
                <DataTable
                    generateKeyFrom={'prospectId'}
                    isLoading={isProspectsLoading}
                    isSelectable={true}
                    columns={columns}
                    rows={filteredProspects}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    selected={selectedRows}
                    setSelected={(selectedRows) => setSelectedRows(selectedRows)}
                />
            </Stack>
            <AlertDialog
                open={openDeleteDialog}
                isLoading={deleteMutation.isPending}
                handleClose={handleCloseDeleteDialog}
                title={`Are you sure you want to archive the ${
                    selectedRows?.length === 1 ? 'prospect' : 'prospects'
                }?`}
                message={
                    selectedRows?.length === 1
                        ? 'By clicking Accept you will archive the selected prospect. To recover it, you will need to contact Lexo Support.'
                        : `By clicking Accept you will archive the selected prospects. To recover them, you will need to contact Lexo Support.`
                }
                handleConfirm={deleteSelectedProspects}
            />
            {isNewIntakeModalOpen && (
                <NewProspect
                    key={prefillPurchaseFromDiscovery ? 'aps-discovery' : 'default'}
                    onClose={handleNewIntakeModalClose}
                    initialMatterType={prefillPurchaseFromDiscovery ? 'Purchase' : undefined}
                />
            )}
            {isNewQuoteAndIntakeModalOpen && (
                <QuoteLinkSentModal onClose={() => setIsNewQuoteAndIntakeModalOpen(false)} />
            )}
        </PageWrapper>
    );
}
